import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, User } from "lucide-react";
import { useLangNavigate } from "@/components/LangLink";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/App";
import { tr } from "@/i18n";

export default function LoginModal({ open, onClose }) {
  const [memberId, setMemberId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetLogin, setResetLogin] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useLangNavigate();

  const close = () => {
    setResetMode(false);
    setResetSent(false);
    setResetLogin("");
    setError("");
    onClose();
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { login: resetLogin });
      setResetSent(true);
    } catch {
      setResetSent(true);
    } finally {
      setResetLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        login: memberId,
        password,
      });
      localStorage.setItem("dc_token", data.token);
      localStorage.setItem("dc_user", JSON.stringify(data.user));
      setUser(data.user);
      setMemberId("");
      setPassword("");
      onClose();
      navigate("/portal");
      toast.success(tr("Welcome back, {{name}}", { name: data.user.name.split(" ")[0] }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? tr(detail) : tr("Unable to sign in. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="login-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={close} />
          <motion.div
            className="relative w-full max-w-md bg-[#0B0B0C]/90 backdrop-blur-xl border border-white/10 px-10 py-12"
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              data-testid="login-modal-close"
              onClick={close}
              className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors duration-300"
              aria-label={tr("Close")}
            >
              <X strokeWidth={1.25} size={22} />
            </button>

            <div className="text-center mb-10">
              <img src="/logo-gold.png" alt={tr("Delux Concierge")} className="h-16 w-auto mx-auto mb-6" />
              <p className="overline-lux">{tr("Member Portal")}</p>
              <h2 className="font-serif text-3xl font-light mt-3 text-white">{resetMode ? tr("Reset password") : tr("Welcome back")}</h2>
            </div>

            {resetMode ? (
              resetSent ? (
                <div className="text-center" data-testid="login-reset-sent">
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-8">{tr("If an account matches those details, a secure reset link is on its way to the member's email address.")}</p>
                  <button
                    data-testid="login-reset-back"
                    onClick={() => { setResetMode(false); setResetSent(false); setResetLogin(""); }}
                    className="text-xs tracking-[0.2em] uppercase text-gold hover:text-gold-bright transition-colors duration-300"
                  >{tr("Back to sign in")}</button>
                </div>
              ) : (
                <form onSubmit={submitReset} className="space-y-7" data-testid="login-reset-form">
                  <div className="relative">
                    <User strokeWidth={1.25} size={15} className="absolute left-0 top-4 text-gold/70" />
                    <input
                      data-testid="login-reset-input"
                      type="text"
                      required
                      value={resetLogin}
                      onChange={(e) => setResetLogin(e.target.value)}
                      placeholder={tr("Member ID or Email")}
                      className="input-lux pl-9"
                    />
                  </div>
                  <button
                    data-testid="login-reset-submit"
                    type="submit"
                    disabled={resetLoading}
                    className="w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
                  >
                    {resetLoading ? tr("Sending…") : tr("Send Reset Link")}
                  </button>
                  <p className="text-center">
                    <button
                      type="button"
                      data-testid="login-reset-cancel"
                      onClick={() => { setResetMode(false); setResetSent(false); setResetLogin(""); }}
                      className="text-xs tracking-[0.2em] uppercase text-white/50 hover:text-gold transition-colors duration-300"
                    >{tr("Back to sign in")}</button>
                  </p>
                </form>
              )
            ) : (
            <>
            <form onSubmit={submit} className="space-y-7">
              <div className="relative">
                <User strokeWidth={1.25} size={15} className="absolute left-0 top-4 text-gold/70" />
                <input
                  data-testid="login-member-id-input"
                  type="text"
                  required
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  placeholder={tr("Member ID or Email")}
                  className="input-lux pl-9"
                />
              </div>
              <div className="relative">
                <Lock strokeWidth={1.25} size={15} className="absolute left-0 top-4 text-gold/70" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={tr("Password")}
                  className="input-lux pl-9"
                />
              </div>
              <p className="text-right -mt-4">
                <button
                  type="button"
                  data-testid="login-forgot-link"
                  onClick={() => setResetMode(true)}
                  className="text-[0.65rem] tracking-[0.15em] uppercase text-white/40 hover:text-gold transition-colors duration-300"
                >{tr("Forgot password?")}</button>
              </p>
              {error && (
                <p data-testid="login-error" className="text-red-400/90 text-xs tracking-wide text-center">
                  {error}
                </p>
              )}
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 hover:bg-gold-bright transition-colors duration-400 disabled:opacity-60"
              >
                {loading ? tr("Signing In…") : tr("Sign In")}
              </button>
            </form>

            <div className="mt-8 text-center">
              <button
                data-testid="login-request-access-link"
                onClick={() => {
                  close();
                  navigate("/contact?type=admission");
                }}
                className="text-xs tracking-[0.2em] uppercase text-white/50 hover:text-gold transition-colors duration-300 border-b border-white/20 hover:border-gold pb-1"
              >{tr("Not a member? Request for admission")}</button>
            </div>
            </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
